import {
  useCallback, useRef, useState,
} from 'react'
import _get from 'lodash/get'
import _set from 'lodash/set'
import _cloneDeep from 'lodash/cloneDeep'

/**
 * Generic Apollo `fetchMore` adaptor (re-entry guard, terminal
 * short-circuit, `updateQuery` concat).
 *
 * Sole consumer: `useProfilePlaylists` (offset, `playlistList.playlists`).
 * The cursor branch is currently unused. Other Community lists
 * (`useFollowedPlaylists`, `useWatchHistoryData`) paginate via a manual
 * no-cache + local-accumulator pattern instead, because this `updateQuery`
 * concat collides with their fields' cache merge (`relayStylePagination`
 * edges / a `userMediaPositionList` typePolicy that already concatenates).
 *
 * Two pagination shapes covered:
 *   - **offset**: `{ ...base, [offsetVar]: listAtPath.length }`, then
 *     `updateQuery` writes back `[...prev, ...next]` at `listPath`.
 *     Terminal: empty next page.
 *   - **cursor**: `{ ...base, [cursorVar]: endCursor }`, then
 *     `updateQuery` writes back `[...prevEdges, ...nextEdges]` at
 *     `edgesPath`. Terminal: `pageInfo.hasNextPage === false`.
 *
 * Inside `updateQuery`, `prev` and `fetchMoreResult` are typed as the
 * caller's `TData` parameter — Apollo's own `fetchMore` contract uses
 * the same generic. The runtime path (`listPath` / `edgesPath`) is a
 * string, so the leaf cast still goes through `unknown[]`; the cast
 * lives at the access boundary, not at the type-alias surface.
 *
 * `lodash/set` + `cloneDeep` are intentional — the plan mandates
 * immutable nested writes via these helpers rather than hand-rolled
 * recursion. `cloneDeep(fetchMoreResult)` is O(payload size) and runs
 * once per `loadMore`; tolerable for `SHELL_PAGE_SIZE` pages.
 */

type OffsetConfig = {
  type: 'offset'
  /** Dotted path to the array in the response (e.g. `'playlistList.playlists'`). */
  listPath: string
  /** Variable name for the offset cursor. Defaults to `'offset'`. */
  offsetVar?: string
  /** Page size — advisory; the consumer must include `limit` in `baseVariables` themselves. */
  pageSize: number
}

type CursorConfig = {
  type: 'cursor'
  /** Dotted path to the edges array (e.g. `'followedPlaylists.edges'`). */
  edgesPath: string
  /** Dotted path to the pageInfo object (e.g. `'followedPlaylists.pageInfo'`). */
  pageInfoPath: string
  /** Variable name for the cursor. Defaults to `'after'`. */
  cursorVar?: string
  pageSize: number
}

export type PaginationConfig = OffsetConfig | CursorConfig

type ApolloFetchMore<TData> = (options: {
  variables: Record<string, unknown>
  updateQuery: (prev: TData, args: { fetchMoreResult: TData }) => TData
}) => Promise<unknown>

interface UseApolloListPaginationResult {
  loadMore: () => Promise<void>
  isLoadingMore: boolean
  hasMore: boolean
}

/**
 * Generic `fetchMore` adaptor. Sole consumer: `useProfilePlaylists`
 * (offset, `playlistList.playlists`). Cursor branch unused — see the
 * module docblock above.
 */
export default function useApolloListPagination<TData = unknown> (
  fetchMore: ApolloFetchMore<TData> | undefined,
  data: TData | undefined,
  config: PaginationConfig,
  baseVariables: Record<string, unknown>,
): UseApolloListPaginationResult {
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Ref-stabilize per-render inputs so `loadMore`'s identity stays
  // constant. The previous useCallback deps `[isLoadingMore, fetchMore,
  // data, config, baseVariables]` re-built loadMore every render because
  //   - Apollo re-emits a new `data` reference on every cache write,
  //   - consumers (useWatchHistoryData, useProfilePlaylists,
  //     useFollowedPlaylists) pass fresh object literals for `config` and
  //     `baseVariables`,
  // so any downstream effect/memo including `loadMore` in its dep array
  // ran on every render. Refs read at call time deliver the freshest
  // values the user expects (current list length for nextOffset, current
  // variables for the fetchMore call) without bumping identity.
  //
  // Trade-off: a future change that needs `loadMore` to react to `config.type`
  // changes after mount would not be served by this pattern — revisit if
  // a consumer needs that.
  //
  // Pagination merge contract: this `updateQuery` concatenates pages. Its only
  // consumer paginates `playlistList.playlists`, which has NO companion typePolicy
  // `merge` in apollo-graphql/index.js — so this is the sole concat (no double-merge).
  const dataRef = useRef(data)
  dataRef.current = data
  const configRef = useRef(config)
  configRef.current = config
  const baseVariablesRef = useRef(baseVariables)
  baseVariablesRef.current = baseVariables
  const isLoadingMoreRef = useRef(isLoadingMore)
  isLoadingMoreRef.current = isLoadingMore

  // hasMore: cursor uses pageInfo authoritative; offset is conservative
  // (loadMore itself short-circuits when last page returned empty).
  let hasMore = false
  if (config.type === 'cursor') {
    hasMore = !!_get(data, `${config.pageInfoPath}.hasNextPage`)
  } else {
    const list = _get(data, config.listPath, []) as unknown[]
    hasMore = Array.isArray(list) && list.length > 0
  }

  const loadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || !fetchMore) return
    const dataNow = dataRef.current
    if (!dataNow) return
    const configNow = configRef.current
    const baseVariablesNow = baseVariablesRef.current

    if (configNow.type === 'offset') {
      const list = _get(dataNow, configNow.listPath, []) as unknown[]
      if (!Array.isArray(list) || list.length === 0) return
      const offsetVar = configNow.offsetVar ?? 'offset'
      const nextOffset = list.length

      setIsLoadingMore(true)
      try {
        await fetchMore({
          variables: { ...baseVariablesNow, [offsetVar]: nextOffset },
          updateQuery: (prev: TData, { fetchMoreResult }: { fetchMoreResult: TData }) => {
            if (!fetchMoreResult) return prev
            const prevList = (_get(prev, configNow.listPath, []) as unknown[]) ?? []
            const nextList = (_get(fetchMoreResult, configNow.listPath, []) as unknown[]) ?? []
            if (!Array.isArray(nextList) || nextList.length === 0) return prev
            const merged = _cloneDeep(fetchMoreResult)
            _set(merged, configNow.listPath, [...prevList, ...nextList])
            return merged
          },
        })
      } finally {
        setIsLoadingMore(false)
      }
      return
    }

    // cursor
    const endCursor = _get(dataNow, `${configNow.pageInfoPath}.endCursor`)
    const pageHasNext = _get(dataNow, `${configNow.pageInfoPath}.hasNextPage`)
    if (!pageHasNext || !endCursor) return
    const cursorVar = configNow.cursorVar ?? 'after'

    setIsLoadingMore(true)
    try {
      await fetchMore({
        variables: { ...baseVariablesNow, [cursorVar]: endCursor },
        updateQuery: (prev: TData, { fetchMoreResult }: { fetchMoreResult: TData }) => {
          if (!fetchMoreResult) return prev
          const prevEdges = (_get(prev, configNow.edgesPath, []) as unknown[]) ?? []
          const nextEdges = (_get(fetchMoreResult, configNow.edgesPath, []) as unknown[]) ?? []
          if (!Array.isArray(nextEdges) || nextEdges.length === 0) return prev
          const merged = _cloneDeep(fetchMoreResult)
          _set(merged, configNow.edgesPath, [...prevEdges, ...nextEdges])
          return merged
        },
      })
    } finally {
      setIsLoadingMore(false)
    }
  }, [fetchMore])

  return { loadMore, isLoadingMore, hasMore }
}
